"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { Bell, Building2, Car, Check, Download, FileText, LogOut, Monitor, Moon, Route, Search, Settings2, Share, ShieldCheck, Sun, Truck, UserRound } from "lucide-react";
import type { Notificacao, ResultadoBusca } from "@/types";
import { can, useEmpresa, useSession } from "@/core/session";
import { SCREEN_META } from "@/core/screen-meta";
import { openScreen } from "@/core/tabs";
import { MENUS } from "@/shell/menus";
import { useUi } from "@/shell/ui-store";
import { usePrefs, type ThemePref } from "@/core/prefs";
import { api } from "@/lib/api";
import { useApiQuery, useInvalidate } from "@/lib/query";
import { fmt } from "@/lib/format";
import { cn } from "@/lib/utils";
import { EnvBadge, ToneIcon } from "@/components/status";
import { APP_VERSION } from "@/features/auth/components";
import { LogoMarca } from "@/components/logo";
import { resetNav, useInstall, useNav } from "../nav";
import { AvatarEmpresa, Card, List, ListSkeleton, MEmpty, Page, Row, SearchField, Section, Sheet } from "../ui";

const PAPEL = { admin: "Admin", operador: "Operador", financeiro: "Financeiro", leitura: "Leitura" } as const;

export function MMais() {
  const router = useRouter();
  const qc = useQueryClient();
  const usuario = useSession((s) => s.usuario);
  const empresas = useSession((s) => s.empresas);
  const empresa = useEmpresa();
  const theme = usePrefs((s) => s.theme);
  const setTheme = usePrefs((s) => s.setTheme);
  const install = useInstall();
  const [empSheet, setEmpSheet] = React.useState(false);
  const [iosSheet, setIosSheet] = React.useState(false);
  if (!usuario || !empresa) return null;
  const iniciais = usuario.nome
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const sair = async () => {
    await api("/auth/logout", { method: "POST" }).catch(() => null);
    useSession.getState().logout();
    resetNav();
    router.replace("/login");
  };

  return (
    <Page title="Menu" leading={<LogoMarca className="size-7 text-primary" />}>
      <Section>
        <Card className="flex items-center gap-3.5 p-4">
          <span className="inline-flex size-12 items-center justify-center rounded-full bg-primary text-base font-semibold text-primary-fg">{iniciais}</span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[17px] font-semibold">{usuario.nome}</span>
            <span className="block truncate text-sm text-muted">{usuario.email}</span>
          </span>
          <span className="rounded-full bg-neutral-soft px-2.5 py-1 text-xs font-medium text-neutral">{PAPEL[usuario.papel]}</span>
        </Card>
      </Section>

      {MENUS.map((m) => {
        const itens = m.items.filter((it) => !it.screen || !SCREEN_META[it.screen].acao || can(usuario.papel, SCREEN_META[it.screen].acao!));
        if (!itens.length) return null;
        return (
          <Section key={m.id} title={m.label}>
            <List>
              {itens.map((it) => (
                <Row
                  key={it.label}
                  icon={it.icon}
                  title={it.label}
                  onClick={() => {
                    if (it.action === "encerrar-viagem") useUi.getState().setEncerrar(true);
                    else if (it.screen) openScreen(it.screen, it.params);
                  }}
                />
              ))}
            </List>
          </Section>
        );
      })}

      <Section title="Empresa">
        <List>
          <Row
            leading={<AvatarEmpresa empresa={empresa} className="size-9" />}
            title={empresa.nomeFantasia}
            subtitle={fmt.cnpj(empresa.cnpj)}
            trailing={<EnvBadge ambiente={empresa.ambiente} />}
            onClick={empresas.length > 1 ? () => setEmpSheet(true) : undefined}
          />
        </List>
      </Section>

      <Section title="Aparência">
        <div className="grid grid-cols-3 gap-2">
          {(
            [
              ["light", "Claro", Sun],
              ["dark", "Escuro", Moon],
              ["system", "Sistema", Monitor],
            ] as const
          ).map(([v, l, Icon]) => (
            <button
              key={v}
              type="button"
              onClick={() => setTheme(v as ThemePref)}
              className={cn(
                "flex h-20 flex-col items-center justify-center gap-1.5 rounded-xl border text-sm font-medium",
                theme === v ? "border-primary bg-primary-soft text-fg" : "border-border bg-surface text-muted active:bg-row-hover",
              )}
            >
              <Icon className="size-5" />
              {l}
            </button>
          ))}
        </div>
      </Section>

      <Section title="App">
        <List>
          <Row icon={Bell} title="Notificações" onClick={() => useNav.getState().push("notificacoes")} />
          <Row icon={Search} title="Buscar documento" onClick={() => useNav.getState().push("busca")} />
          <Row icon={Settings2} title="Preferências" onClick={() => openScreen("preferencias")} />
          {usuario.superadmin ? <Row icon={ShieldCheck} title="Administração do SaaS" subtitle="Contas, faturas e certificados" onClick={() => router.push("/admin")} /> : null}
          {!install.standalone && install.disponivel ? <Row icon={Download} title="Instalar aplicativo" subtitle="Adicionar à tela inicial" onClick={() => void install.instalar()} /> : null}
          {!install.standalone && !install.disponivel && install.ios ? <Row icon={Share} title="Instalar aplicativo" subtitle="Adicionar à Tela de Início" onClick={() => setIosSheet(true)} /> : null}
        </List>
      </Section>

      <Section className="pb-8">
        <List>
          <Row icon={LogOut} title="Sair" danger chevron={false} onClick={sair} />
        </List>
        <p className="mt-4 text-center text-xs text-subtle">TMS {APP_VERSION}</p>
      </Section>

      <Sheet open={empSheet} onOpenChange={setEmpSheet} title="Trocar empresa">
        <List className="mb-2">
          {empresas.map((e) => (
            <Row
              key={e.id}
              title={e.nomeFantasia}
              subtitle={fmt.cnpj(e.cnpj)}
              chevron={false}
              trailing={e.id === empresa.id ? <Check className="size-5 text-primary" /> : <EnvBadge ambiente={e.ambiente} />}
              onClick={() => {
                if (e.id !== empresa.id) {
                  useSession.getState().selectEmpresa(e.id);
                  qc.removeQueries();
                  resetNav();
                }
                setEmpSheet(false);
              }}
            />
          ))}
        </List>
      </Sheet>

      <Sheet open={iosSheet} onOpenChange={setIosSheet} title="Instalar no iPhone">
        <ol className="mb-4 space-y-3 text-[15px]">
          <li className="flex items-center gap-3">
            <span className="inline-flex size-8 items-center justify-center rounded-full bg-surface-2 text-sm font-semibold">1</span>
            Toque em <Share className="inline size-5 text-primary" /> Compartilhar
          </li>
          <li className="flex items-center gap-3">
            <span className="inline-flex size-8 items-center justify-center rounded-full bg-surface-2 text-sm font-semibold">2</span>
            Escolha &ldquo;Adicionar à Tela de Início&rdquo;
          </li>
        </ol>
      </Sheet>
    </Page>
  );
}

export function MNotificacoes() {
  const q = useApiQuery<Notificacao[]>("notificacoes", "/notificacoes");
  const invalidate = useInvalidate();
  const abrir = async (n: Notificacao) => {
    if (!n.lida) {
      await api(`/notificacoes/${n.id}/lida`, { method: "POST" }).catch(() => null);
      invalidate("notificacoes");
    }
    const a = n.alvo;
    const nav = useNav.getState();
    if (a?.screen === "viagem" && a.params?.id) nav.push("viagem", { id: a.params.id });
    else if (a?.screen === "mdfes") nav.setTab("documentos", { tipo: "mdfe" });
    else if (a?.screen === "ciots") nav.setTab("documentos", { tipo: "ciot" });
  };
  const naoLidas = (q.data ?? []).filter((n) => !n.lida).length;
  return (
    <Page
      back
      title="Notificações"
      onRefresh={() => q.refetch()}
      actions={
        naoLidas ? (
          <button
            type="button"
            className="px-3 text-sm font-medium text-primary"
            onClick={async () => {
              await api("/notificacoes/lidas", { method: "POST" }).catch(() => null);
              invalidate("notificacoes");
            }}
          >
            Ler todas
          </button>
        ) : null
      }
    >
      <div className="px-4 pt-4 pb-6">
        {!q.data ? (
          <ListSkeleton />
        ) : !q.data.length ? (
          <MEmpty icon={Bell} text="Nenhuma notificação" />
        ) : (
          <List>
            {q.data.map((n) => (
              <Row
                key={n.id}
                title={<span className={cn(!n.lida && "font-semibold")}>{n.titulo}</span>}
                subtitle={fmt.dateTime(n.data)}
                trailing={
                  <span className="flex items-center gap-2">
                    {!n.lida ? <span className="size-2 rounded-full bg-primary" /> : null}
                    <ToneIcon tom={n.tom} />
                  </span>
                }
                chevron={false}
                onClick={() => abrir(n)}
              />
            ))}
          </List>
        )}
      </div>
    </Page>
  );
}

const ICONE = { cte: FileText, viagem: Route, veiculo: Car, tomador: Building2, mdfe: Truck } as const;

export function MBusca() {
  const [q, setQ] = React.useState("");
  const [res, setRes] = React.useState<ResultadoBusca[] | null>(null);
  React.useEffect(() => {
    const termo = q.trim();
    if (termo.length < 2) return;
    let vivo = true;
    const t = setTimeout(() => {
      api<ResultadoBusca[]>("/busca", { query: { q: termo } })
        .then((r) => vivo && setRes(r))
        .catch(() => vivo && setRes([]));
    }, 250);
    return () => {
      vivo = false;
      clearTimeout(t);
    };
  }, [q]);
  const abrir = (r: ResultadoBusca) => {
    const nav = useNav.getState();
    if (r.tipo === "viagem") nav.push("viagem", { id: r.id });
    else if (r.tipo === "cte") nav.push("cte", { id: r.id });
    else if (r.tipo === "mdfe") nav.push("mdfe", { id: r.id });
  };
  return (
    <Page back title="Buscar">
      <div className="px-4 pt-3 pb-6">
        <SearchField
          value={q}
          onChange={(v) => {
            setQ(v);
            if (v.trim().length < 2) setRes(null);
          }}
          placeholder="Nº da viagem, CT-e, MDF-e, chave"
          autoFocus
        />
        <div className="mt-3">
          {res === null ? (
            <MEmpty icon={Search} text="Digite ao menos 2 caracteres" />
          ) : !res.length ? (
            <MEmpty icon={Search} text="Nada encontrado" />
          ) : (
            <List>
              {res.map((r) => {
                const Icon = ICONE[r.tipo] ?? UserRound;
                const navegavel = r.tipo === "viagem" || r.tipo === "cte" || r.tipo === "mdfe";
                return <Row key={`${r.tipo}-${r.id}`} icon={Icon} title={r.titulo} subtitle={r.subtitulo} onClick={navegavel ? () => abrir(r) : undefined} />;
              })}
            </List>
          )}
        </div>
      </div>
    </Page>
  );
}
